import {
  addMonths,
  addWeeks,
  endOfMonth,
  endOfWeek,
  isAfter,
  isValid,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import moment from 'moment';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { actionTypes } from './actionTypes';

const defaultEarliestDate = new Date('2019-01-06');

const getDefaultDateRange = () => ({
  startDate: startOfMonth(addMonths(new Date(), -1)),
  endDate: endOfMonth(addMonths(new Date(), -1)),
});

/**
 * Validates and normalizes a date range to ensure startDate <= endDate.
 * Returns the default date range if validation fails.
 */
const validateDateRange = (dateRange) => {
  if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
    return getDefaultDateRange();
  }

  // Parse dates if they're strings (from localStorage rehydration)
  const startDate = typeof dateRange.startDate === 'string' 
    ? new Date(dateRange.startDate) 
    : dateRange.startDate;
  const endDate = typeof dateRange.endDate === 'string' 
    ? new Date(dateRange.endDate) 
    : dateRange.endDate;

  // Check if dates are valid
  if (!isValid(startDate) || !isValid(endDate)) {
    return getDefaultDateRange();
  }

  // Check if dates are in correct order (startDate should be before or equal to endDate)
  if (isAfter(startDate, endDate)) {
    return getDefaultDateRange();
  }

  return { startDate, endDate };
};

const initialAuthState = {
  latestDateMonthly: startOfMonth(addMonths(new Date(), -2)).toISOString(),
  latestDateWeekly: startOfWeek(addWeeks(new Date(), -2)).toISOString(),
  earliestDateMonthly: startOfMonth(defaultEarliestDate).toISOString(),
  earliestDateWeekly: startOfWeek(defaultEarliestDate).toISOString(),
  latestDateDaily: subDays(new Date(), 1).toISOString(),
  earliestDateDaily: subDays(defaultEarliestDate, 1).toISOString(),
  dateRange: getDefaultDateRange(),
  rangeType: 'Monthly',
  labelSelected: 'Last Month',
  isChangeRangeType: false,
};

export const reducer = persistReducer(
  {
    storage,
    key: 'date-range',
    whitelist: [
      'dateRange',
      'latestDateMonthly',
      'latestDateWeekly',
      'earliestDateMonthly',
      'earliestDateWeekly',
      'rangeType',
      'labelSelected',
    ],
  },
  (state = initialAuthState, action) => {
    switch (action.type) {
      // Handle rehydration from localStorage - validate date range
      case 'persist/REHYDRATE': {
        if (action.key !== 'date-range' || !action.payload) {
          return state;
        }
        const rehydratedDateRange = validateDateRange(action.payload.dateRange);
        return {
          ...state,
          ...action.payload,
          dateRange: rehydratedDateRange,
        };
      }

      case actionTypes.UpdateLatestDate: {
        const { latest_month, latest_week, earliest_month, earliest_week } =
          action.payload;
        if (latest_month && latest_week) {
          const mLatestMonthly = moment(latest_month);
          const mLatestWeekly = moment(latest_week);
          const mEarliestMonth = moment(earliest_month);
          const mEarliestWeek = moment(earliest_week);
          const latest_monthly_month = mLatestMonthly.format('MM');
          const latest_monthly_year = mLatestMonthly.format('YYYY');
          const latest_weekly_month = mLatestWeekly.format('MM');
          const latest_weekly_date = mLatestWeekly.format('DD');
          const latest_weekly_year = mLatestWeekly.format('YYYY');

          let monthly = new Date(
              +latest_monthly_year,
              +latest_monthly_month - 1,
              1,
            ),
            weekly = new Date(
              +latest_weekly_year,
              +latest_weekly_month - 1,
              +latest_weekly_date,
            );
          let dateRange = state.dateRange;
          const currentStartDate = new Date(dateRange.startDate);
          const currentEndDate = new Date(dateRange.endDate);
          const rangeType = state.rangeType.toLowerCase();
          
          // Update dateRange when current selection is after the latest available data
          if (rangeType === 'monthly') {
            // Compare by year and month to avoid timezone issues
            const currentStartYear = currentStartDate.getFullYear();
            const currentStartMonth = currentStartDate.getMonth();
            const currentYear = currentEndDate.getFullYear();
            const currentMonth = currentEndDate.getMonth();
            const latestYear = monthly.getFullYear();
            const latestMonth = monthly.getMonth();
            
            // Reset if either start date OR end date is after the latest available month
            // This ensures the selection is always valid even if only one date is out of range
            const isAfterLatestMonth =
              currentStartYear > latestYear ||
              (currentStartYear === latestYear && currentStartMonth > latestMonth) ||
              currentYear > latestYear ||
              (currentYear === latestYear && currentMonth > latestMonth);
            
            if (isAfterLatestMonth) {
              dateRange = {
                startDate: startOfMonth(monthly),
                endDate: endOfMonth(monthly),
              };
            }
          } else if (rangeType === 'weekly') {
            // Compare dates using getTime() for explicit millisecond comparison (avoids timezone edge cases)
            // Reset if either start date OR end date is after the latest available week
            const latestWeekEnd = endOfWeek(weekly);
            
            if (
              currentStartDate.getTime() > latestWeekEnd.getTime() ||
              currentEndDate.getTime() > latestWeekEnd.getTime()
            ) {
              dateRange = {
                startDate: startOfWeek(weekly),
                endDate: endOfWeek(weekly),
              };
            }
          }
          return {
            ...state,
            dateRange,
            earliestDateMonthly: mEarliestMonth,
            earliestDateWeekly: mEarliestWeek,
            latestDateMonthly: monthly.toISOString(),
            latestDateWeekly: weekly.toISOString(),
          };
        }
        return state;
      }

      case actionTypes.SetDateRange: {
        const newDateRange = validateDateRange({
          startDate: action.payload.startDate,
          endDate: action.payload.endDate,
        });
        return {
          ...state,
          dateRange: newDateRange,
        };
      }

      case actionTypes.SetRangeType: {
        return {
          ...state,
          rangeType: action.payload,
        };
      }

      case actionTypes.SetLabelSelected: {
        return {
          ...state,
          labelSelected: action.payload,
        };
      }

      case actionTypes.ChangeRangeType: {
        return {
          ...state,
          isChangeRangeType: action.payload,
        };
      }

      default:
        return state;
    }
  },
);
